import './ChartActionsMenu.scss';
import { ReactElement } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import {
  MdCropLandscape,
  MdCropSquare,
  MdDeleteOutline,
  MdOutlineFileDownload,
  MdViewColumn,
  MdViewWeek
} from 'react-icons/md';
import {
  CARD_SIZES,
  cardSizesForChartType,
  CardSize,
  ChartConfig,
  QueryResponse
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Placement, Size, SizeType, StretchMode } from '@Components/designConstants';
import { Dropdown, DropdownToggleType, MenuItem } from '@Components/Dropdown';
import { exportMultiSheetToExcel } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { chartExportFileName, chartExportSheets, chartHasData } from './chart-actions-menu-utils';
import { useFieldMetaCtx } from './useFieldMetaCtx';

const ChartAction = {
  DOWNLOAD_XLSX: 'download-xlsx',
  REMOVE_FROM_DASHBOARD: 'remove-from-dashboard'
} as const;
type ChartActionType = (typeof ChartAction)[keyof typeof ChartAction];

// A dashboard card's preset size is chosen from this same menu, so a row's value is either a chart
// action or a size name. The two vocabularies can't collide — ChartAction's values are verbs.
type MenuAction = ChartActionType | CardSize;

const CARD_SIZE_LABELS: Record<CardSize, string> = {
  'one-third': 'Small (one-third width)',
  standard: 'Standard (half width)',
  'two-thirds': 'Large (two-thirds width)',
  wide: 'Wide (full width)'
};

const CARD_SIZE_ICONS: Record<CardSize, ReactElement> = {
  'one-third': <MdViewColumn />,
  standard: <MdCropSquare />,
  'two-thirds': <MdViewWeek />,
  wide: <MdCropLandscape />
};

// Lands on the toggle button; `<MENU_CLASS>-popup` goes on the open menu (see ChartActionsMenu.scss).
const MENU_CLASS = 'chart-actions-menu';

interface ChartActionsMenuProps {
  config: ChartConfig;
  data: QueryResponse;
  // Take this chart off the surface it's rendered on. Supplied only by a dashboard card — the focused
  // chart view shows a chart on its own, with no placement to remove — and it gates the menu entry:
  // no handler, no entry. The caller owns the confirm; this menu just invokes it.
  onRemoveFromDashboard?: () => void;
  // Shows the remove entry disabled, e.g. while a dashboard save is in flight.
  removeDisabled?: boolean;
  // The card's CURRENT preset size, and the handler that changes it. Supplied only by a dashboard
  // card — a chart on its own has no placement to size — and, like onRemoveFromDashboard, the
  // handler gates the rows: no handler, no size section.
  cardSize?: CardSize;
  onChangeCardSize?: (size: CardSize) => void;
  size?: SizeType;
}

// A chart's overflow (⋮) menu: download this chart's underlying data as an Excel workbook, plus —
// on a dashboard card only — remove it from the dashboard. Each further per-chart action is one more
// entry in `options` below.
//
// It renders into the surrounding container's existing header actions (the dashboard card's action
// row, the focused chart view's header) rather than inside the chart body. Two reasons, the second
// of which matters if you are here to add something:
//   1. Every other action on those surfaces already lives there, so this reads as part of the set.
//   2. An action needing the CARD rather than the chart — PNG export being the expected next one,
//      since an image without the card's title isn't much use — needs an element the chart body
//      cannot reach, because the container owns the title. From the header, such an action is an
//      additive `captureRef` prop fed by DashboardCard's existing cardRef (it already holds one for
//      drag) or a new ref on SingleChartView's root. Moving this menu down into MetricPickerChart
//      would look like a simplification and would quietly cost that.
//      Two traps for whoever adds it. downloadPNG takes a SELECTOR, which resolves to the first
//      match — fine for a one-off chart, wrong for a dashboard where every card would export the
//      top-left one, so it needs widening to accept the element. And html2canvas clones the DOM
//      synchronously inside the click handler, before React has closed the menu, so
//      `.chart-actions-menu-popup` has to go in downloadPNG's strip list or the open menu is baked
//      into the image on top of the chart.
//
// The 3-dot toggle is the shared Dropdown in its icon-only form (`leadingIcon` + `hideCaret` + an
// empty toggle), the same composition the agent session list uses for its row menu.
export const ChartActionsMenu = ({
  config,
  data,
  onRemoveFromDashboard,
  removeDisabled,
  cardSize,
  onChangeCardSize,
  size = Size.MD
}: ChartActionsMenuProps) => {
  const { cid: company } = useCompanyInfo();
  const ctx = useFieldMetaCtx(config);

  // Sheets are built on demand rather than on render: for a wide table that is real work, and a menu
  // nobody opens should cost nothing.
  const downloadXlsx = () =>
    exportMultiSheetToExcel(
      chartExportSheets(config, data, ctx),
      chartExportFileName(company, config.title)
    );

  // Keyed by action rather than switched on inline: adding a member to ChartAction without a handler
  // for it is then a compile error, instead of a menu entry that silently runs the wrong action.
  const handlers: Record<ChartActionType, () => void> = {
    [ChartAction.DOWNLOAD_XLSX]: downloadXlsx,
    // Unreachable without a handler: the entry below is only rendered when one is supplied.
    [ChartAction.REMOVE_FROM_DASHBOARD]: onRemoveFromDashboard ?? (() => {})
  };

  // A size row is dispatched by name rather than through `handlers`, which stays exhaustive over
  // ChartAction alone — that exhaustiveness is what makes a new chart action a compile error.
  const handleAction = (action: MenuAction) => {
    const picked = CARD_SIZES.find(s => s === action);
    if (picked) {
      onChangeCardSize?.(picked);
      return;
    }
    handlers[action as ChartActionType]();
  };

  // Sizes this chart's TYPE allows (e.g. a table can't go to `one-third`) -- see
  // CARD_SIZING_BY_CHART_TYPE in bpm-types. The card's current size is kept in the list even when
  // the matrix disallows it: a placement can hold a size its type no longer permits (saved before
  // the matrix existed, or from a hand-edited row), and dropping it would make the menu show
  // nothing selected -- silently misreporting what the card IS. Filtering CARD_SIZES (rather than
  // concatenating the matrix's allowed list with the current size) keeps the offered rows in the
  // same ascending-width order CARD_SIZES already defines.
  const allowedCardSizes = cardSizesForChartType(config.type);
  const offeredCardSizes = CARD_SIZES.filter(s => allowedCardSizes.includes(s) || s === cardSize);

  const options: MenuItem<MenuAction>[] = [
    {
      value: ChartAction.DOWNLOAD_XLSX,
      label: 'Download XLSX',
      leadingContent: <MdOutlineFileDownload />,
      disabled: !chartHasData(data)
    },
    // Card size. With two sizes this listed everything EXCEPT the current one, so the single row
    // shown was itself the action. Three sizes breaks that: "Small" and "Standard" side by side do
    // not say which one you are on. So every OFFERED size is listed, with the current one marked
    // selected and disabled -- a picker rather than a verb, and no onClick guard (the disabled prop
    // owns it).
    ...(onChangeCardSize
      ? offeredCardSizes.map(s => ({
          value: s,
          label: CARD_SIZE_LABELS[s],
          leadingContent: CARD_SIZE_ICONS[s],
          selected: s === cardSize,
          disabled: s === cardSize
        }))
      : []),
    // Last, and styled apart, so the one destructive action can't be hit while reaching for an export.
    ...(onRemoveFromDashboard
      ? [
          {
            value: ChartAction.REMOVE_FROM_DASHBOARD,
            label: 'Remove from dashboard',
            leadingContent: <MdDeleteOutline />,
            className: 'chart-action-destructive',
            disabled: removeDisabled
          }
        ]
      : [])
  ];

  return (
    <Dropdown
      type={DropdownToggleType.ICON_ONLY}
      overlayPlacement={Placement.RIGHT}
      className={MENU_CLASS}
      dropdownMenuClassName={`${MENU_CLASS}-popup`}
      ariaLabel='Chart actions'
      options={options}
      onChange={handleAction}
      leadingIcon={<BsThreeDotsVertical />}
      placeholder=''
      size={size}
      stretch={StretchMode.FIT}
      menuAlign='end'
    />
  );
};
